import React, { useEffect, useState } from 'react';

import { Spinner } from '@components';
import { STORAGE_KEYS } from '@constants/storage';
import useNotify from '@hooks/notify';
import ConfirmationModal from '@layouts/modal/ConfirmationModal';
import { getLocalStorageItem } from '@utils/storage';

import CalendarIntegrationContent from './CalendarIntegrationContent';
import CalendarIntegrationDetailsModal from './CalendarIntegrationDetailsModal';
import { SyncPreferences, SyncPreferenceType } from './types';

import {
  useGetGoogleAuthStatusQuery,
  useGetUserOrgSettingsQuery,
  useLazyGetGoogleAuthUrlQuery,
  useRevokeGoogleAuthMutation,
  useUpdateUserOrgSettingsMutation
} from '../../queries/LeadManagementQuery';

const CalendarIntegrationCard = () => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { createSuccessAlert, createErrorAlert } = useNotify();

  const {
    data: userOrgSettingsData,
    isLoading: isLoadingSettings,
    error: settingsError,
    refetch: refetchSettings
  } = useGetUserOrgSettingsQuery({ organizationId }, { skip: !organizationId });

  const [updateUserOrgSettings, { isLoading: isUpdating }] =
    useUpdateUserOrgSettingsMutation();

  const [revokeGoogleAuth, { isLoading: isRevoking }] =
    useRevokeGoogleAuthMutation();

  const {
    data: googleAuthData,
    isLoading: isLoadingGoogleAuth,
    error: googleAuthError,
    refetch
  } = useGetGoogleAuthStatusQuery(
    { organizationId },
    { skip: !organizationId }
  );

  const [getGoogleAuthUrl, { isLoading: isLoadingAuthUrl }] =
    useLazyGetGoogleAuthUrlQuery();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] =
    useState<boolean>(false);
  const [syncPreferences, setSyncPreferences] = useState<SyncPreferences>({
    task: false,
    event: false
  });

  const isConnected = googleAuthData?.is_authenticated || false;
  const connectedEmail = googleAuthData?.google_email || '';

  const handleViewDetails = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);

    const urlParams = new URLSearchParams(window.location.search || '');
    if (urlParams.has('success')) {
      urlParams.delete('success');
      const newUrl = `${window.location.pathname}${
        urlParams.toString() ? `?${urlParams.toString()}` : ''
      }`;
      window.history.replaceState({}, '', newUrl);
    }

    if (localStorage.getItem(STORAGE_KEYS.AUTHENTICATION_REDIRECTION)) {
      localStorage.removeItem(STORAGE_KEYS.AUTHENTICATION_REDIRECTION);
    }

    if (userOrgSettingsData) {
      setSyncPreferences({
        task: userOrgSettingsData?.task_calendar_sync_enabled,
        event: userOrgSettingsData?.event_calendar_sync_enabled
      });
    }
  };

  const handleSyncPreferenceChange = (
    type: SyncPreferenceType,
    value: boolean
  ) => {
    setSyncPreferences((prev) => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateUserOrgSettings({
        organizationId,
        body: {
          task_calendar_sync_enabled: syncPreferences.task,
          event_calendar_sync_enabled: syncPreferences.event
        }
      }).unwrap();

      refetchSettings();
      setIsModalOpen(false);
      createSuccessAlert('Sync preferences updated successfully!');
      localStorage.removeItem(STORAGE_KEYS.AUTHENTICATION_REDIRECTION);
    } catch (error) {
      createErrorAlert('Failed to update sync preferences.');
      localStorage.removeItem(STORAGE_KEYS.AUTHENTICATION_REDIRECTION);
    }
  };

  const handleConnection = async () => {
    if (isConnected) {
      setShowDisconnectConfirm(true);
    } else {
      try {
        const redirectUrl = encodeURIComponent(
          `${window.location.href}?success=true`
        );
        const result = await getGoogleAuthUrl({
          organizationId,
          redirectUrl
        }).unwrap();

        const authUrl = result?.auth_url;

        localStorage.setItem(STORAGE_KEYS.AUTHENTICATION_REDIRECTION, 'true');

        if (authUrl) {
          window.location.href = authUrl;
        } else {
          throw new Error('No auth URL received from server');
        }
      } catch (error) {
        createErrorAlert(
          'Failed to connect to Google Calendar. Please try again.'
        );
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await revokeGoogleAuth({ organizationId }).unwrap();
      refetch();
      setShowDisconnectConfirm(false);
      createSuccessAlert('Google Calendar disconnected successfully!');
    } catch (error) {
      createErrorAlert('Failed to disconnect Google Calendar.');
    }
  };

  const isSaveDisabled = () => {
    if (!googleAuthData || !userOrgSettingsData) return true;

    const apiPreferences = {
      task: userOrgSettingsData?.task_calendar_sync_enabled,
      event: userOrgSettingsData?.event_calendar_sync_enabled
    };

    return (
      syncPreferences.task === apiPreferences.task &&
      syncPreferences.event === apiPreferences.event
    );
  };

  useEffect(() => {
    if (userOrgSettingsData) {
      setSyncPreferences({
        task: userOrgSettingsData?.task_calendar_sync_enabled,
        event: userOrgSettingsData?.event_calendar_sync_enabled
      });
    }
  }, [userOrgSettingsData]);

  useEffect(() => {
    if (googleAuthError) {
      createErrorAlert('Unable to verify Google Calendar connection status');
    }
    if (settingsError) {
      createErrorAlert('Failed to load user settings');
    }
  }, [googleAuthError, settingsError, createErrorAlert]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search || '');
    const isSuccess = urlParams.get('success');
    const isRedirection = localStorage.getItem(
      STORAGE_KEYS.AUTHENTICATION_REDIRECTION
    );

    if (isSuccess === 'true' && isRedirection === 'true') {
      setIsModalOpen(true);
    }
  }, []);

  return (
    <>
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
        <h2 className="border-b border-grey30 p-6 text-lg font-semibold text-greyText1">
          Google Calendar Integration
        </h2>

        <div className="p-6">
          {isLoadingGoogleAuth || isLoadingSettings ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <CalendarIntegrationContent
              isConnected={isConnected}
              onViewDetails={handleViewDetails}
              handleConnection={handleConnection}
              isLoading={isRevoking || isLoadingAuthUrl}
            />
          )}
        </div>
      </div>

      {isModalOpen && (
        <CalendarIntegrationDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          syncPreferences={syncPreferences}
          onSyncPreferenceChange={handleSyncPreferenceChange}
          onSave={handleSave}
          connectedEmail={connectedEmail}
          isSaveDisabled={isSaveDisabled()}
          isLoading={isUpdating}
        />
      )}
      {showDisconnectConfirm && (
        <ConfirmationModal
          isOpen={showDisconnectConfirm}
          title="Disconnect Google Calendar?"
          message="Are you sure you want to disconnect your Google Calendar? This will stop all calendar synchronization."
          confirmButtonLabel="Disconnect"
          confirmButtonStyle="text-error"
          confirmButtonLabelStyle="!text-errorLite3"
          handleConfirm={handleDisconnect}
          handleCancel={() => setShowDisconnectConfirm(false)}
          isConfirmButtonLoading={isRevoking}
          className="max-w-96"
        />
      )}
    </>
  );
};

export default CalendarIntegrationCard;
